import React, { useState, useEffect, useCallback } from 'react';
import { X, Minimize2, MessageCircle, Bell } from 'lucide-react';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePopupNotifications } from '../../hooks/usePopupNotifications';
import { useChatLayoutPrefs } from '../../hooks/useChatLayoutPrefs';
import { BordaDeResize, redimensionarPelaBorda } from './chatLayout';
import ChatInterface from './ChatInterface';
import PopupNotifications from './PopupNotifications';
import './ChatPopup.css';

// Todas as bordas e cantos redimensionam. Antes só havia direita, baixo e o
// canto inferior direito, com 4px transparentes — quem punha o popup no canto
// da tela nao tinha por onde pegar.
const BORDAS: BordaDeResize[] = [
  'top',
  'right',
  'bottom',
  'left',
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

export const ChatPopup: React.FC = () => {
  const {
    isOpen,
    isMinimized,
    closeChat,
    minimizeChat
  } = useChatPopup();

  const { hasNewMessages } = usePopupNotifications();
  const { user } = useAuth();
  const { layout, moverPara, ajustarCaixa, redimensionarLista, alternarLista } =
    useChatLayoutPrefs(user?.id);
  const { position, size } = layout;

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as Element).closest('.chat-popup-header')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    // O clamp para dentro da tela é do próprio layout (ajustarLayout).
    moverPara({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
  }, [isDragging, dragOffset, moverPara]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Um handler para as oito alças; a matemática de cada borda fica em
  // chatLayout.redimensionarPelaBorda.
  const iniciarResize = (e: React.MouseEvent, borda: BordaDeResize) => {
    e.preventDefault();
    e.stopPropagation();
    const mouse = { x: e.clientX, y: e.clientY };
    const inicio = { position, size };

    const mover = (ev: MouseEvent) => {
      ajustarCaixa(
        redimensionarPelaBorda({
          borda,
          inicio,
          deslocamento: { dx: ev.clientX - mouse.x, dy: ev.clientY - mouse.y },
          viewport: { width: window.innerWidth, height: window.innerHeight },
        })
      );
    };

    const parar = () => {
      document.removeEventListener('mousemove', mover);
      document.removeEventListener('mouseup', parar);
      document.body.classList.remove('chat-resizing');
    };

    document.body.classList.add('chat-resizing');
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', parar);
  };

  // Minimizado, o chat vira só a pílula do ChatFloatingButton (canto inferior
  // direito). Antes o popup também desenhava a própria barra de título na
  // posição salva — ficava solta em cima do menu lateral, com um segundo par
  // de botões, e o usuário via o chat "em dois lugares".
  if (!isOpen || isMinimized) return null;

  // Detectar mobile na hora do render
  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    return (
      <div className="chat-popup mobile">
        <div className="chat-popup-content mobile">
          <div className="chat-popup-header mobile">
            <h3 className="text-lg font-semibold">Chat</h3>
            <button
              onClick={closeChat}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Fechar chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="chat-popup-body mobile">
            <ChatInterface
              duasColunas={false}
              larguraDaLista={layout.larguraDaLista}
              listaColapsada={false}
              onRedimensionarLista={redimensionarLista}
              onAlternarLista={alternarLista}
            />
          </div>
        </div>
      </div>
    );
  }

  // Desktop: popup flutuante
  return (
    <div
      className="chat-popup desktop"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      <div
        className="chat-popup-content desktop"
        onMouseDown={handleMouseDown}
      >
        <div className="chat-popup-header desktop">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-coral" />
            <h3 className="text-sm font-semibold">Chat</h3>
            {hasNewMessages && (
              <div className="flex items-center gap-1">
                <Bell className="w-4 h-4 text-nk-warn animate-pulse" />
                <span className="text-xs text-nk-warn font-medium">Nova mensagem</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={minimizeChat}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Minimizar"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={closeChat}
              className="p-1 hover:bg-red-100 hover:text-red-600 rounded transition-colors"
              title="Fechar chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="chat-popup-body desktop">
          <ChatInterface
            duasColunas
            larguraDaLista={layout.larguraDaLista}
            listaColapsada={layout.listaColapsada}
            onRedimensionarLista={redimensionarLista}
            onAlternarLista={alternarLista}
          />
        </div>

        {/* Notificações dentro do popup */}
        <PopupNotifications />

        {/* Alças de redimensionamento: bordas e cantos */}
        {BORDAS.map(borda => (
          <div
            key={borda}
            className={`resize-handle ${borda}`}
            onMouseDown={e => iniciarResize(e, borda)}
            title="Arraste para redimensionar"
          />
        ))}
      </div>
    </div>
  );
};

export default ChatPopup;
